import { makeStyles } from '@mui/styles';
import TextField from 'apollo-react/components/TextField/TextField';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import Close from 'apollo-react-icons/Close';
import SearchIcon from 'apollo-react-icons/Search';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { DEBOUNCE_SEARCH_TIME } from '../../../../../constants/Common';
import { ENTITY_MODEL_PICKER_STYLES } from '../../../../../constants/CommonStylesConstants';
import { useFormSelector } from '../../../../../custom-hooks';
import { useTranslation } from '../../../../../custom-hooks/useTranslation';
import { getUITA } from '../../../../../utils/commonUtils';
import { getEntityDefinition, getEntityTranslations } from '../../../../selectors/common.selectors';
import {
  findSingleField,
  getExpandedNodeIds,
  renderNodeTree,
} from '../../../../utils/editor.utils';
import { _find } from '../../../../utils/form-manager.utils';

const renderTree = ({ nodeId, label, childNodes }) => (
  <TreeItem key={nodeId} nodeId={nodeId} label={label} ui-ta={getUITA('DataModelTreeItem', label)}>
    {Array.isArray(childNodes) ? childNodes.map((node) => renderTree(node)) : null}
  </TreeItem>
);
const useStyles = makeStyles(ENTITY_MODEL_PICKER_STYLES);

const Search = ({ handleSearch, clearSearch, handleSelectField }) => {
  const classes = useStyles();
  const searchInput = useRef();
  const translateString = useTranslation();

  const [value, setValue] = useState('');

  useEffect(() => {
    searchInput.current.focus();
  }, []);

  const handleChange = ({ target: { value } }) => {
    const newSearchText = value.length === 1 ? value.trim() : value;

    setValue(newSearchText);
    handleSearch(newSearchText);
  };

  const clearSearchText = () => {
    setValue('');
    searchInput.current.focus();

    clearSearch();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && value) {
      handleSelectField();
    }
  };

  return (
    <TextField
      fullWidth
      ui-ta="DataModelSearch"
      startIcon={<SearchIcon />}
      icon={value !== '' ? <Close className={classes.clearIcon} /> : null}
      iconProps={{ onClick: clearSearchText }}
      placeholder={translateString('Search')}
      name="Search"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyPress}
      inputRef={searchInput}
      className={classes.searchInput}
    />
  );
};

const EntityModel = ({
  change,
  select,
  action,
  translation,
  allowEntity,
  width,
  height,
  showSearch = false,
  showTitle = true,
}) => {
  const classes = useStyles({ width, height });
  const translateString = useTranslation();

  const entityDefinition = useFormSelector(getEntityDefinition);
  const entityTranslations = useSelector(getEntityTranslations);

  const [searchText, setSearchText] = useState('');
  const [expanded, setExpanded] = useState([]);

  const defaultEl = Object.keys(entityDefinition)[0];

  const dataTree = useMemo(() => {
    return [renderNodeTree(entityDefinition, entityTranslations, searchText)];
  }, [entityDefinition, entityTranslations, searchText]);

  useEffect(() => {
    const expandedNodeIds = searchText ? getExpandedNodeIds(dataTree) : [defaultEl];
    setExpanded(expandedNodeIds);
  }, [dataTree, defaultEl, searchText]);

  const [click, setClick] = useState(0);

  const handleSelect = (e, nodeId) => {
    e.stopPropagation();
    setClick((prev) => prev + 1);

    setTimeout(function() {
      setClick(0);
    }, 200);

    const nodeValue = _find(dataTree, 'nodeId', nodeId[0]);

    handleSelectAction({ nodeValue, changeFn: click > 0 ? change : select });
  };

  const handleSelectAction = ({ nodeValue, changeFn }) => {
    if (!changeFn) {
      return;
    }

    if (allowEntity) {
      changeFn({ nodeProperties: nodeValue, caption: nodeValue && nodeValue.valueCaption });
    } else if (nodeValue) {
      action
        ? changeFn(translation ? nodeValue.value : nodeValue.valueCaption)
        : changeFn((prev) => `${prev}${nodeValue.value}`);
    }
  };

  const handleExpandedNodes = (_, nodeIds) => setExpanded(nodeIds);

  const debouncedHandleSearch = useMemo(
    () => _.debounce((value) => setSearchText(value), DEBOUNCE_SEARCH_TIME),
    []
  );

  const clearSearch = () => {
    setSearchText('');
  };

  const handleSelectField = () => {
    const selectedField = !!searchText && findSingleField(dataTree);

    selectedField && handleSelectAction({ nodeValue: selectedField, changeFn: change });
  };

  const showDataModelData = !!dataTree[0].childNodes?.length;

  return (
    <div className={classes.mainContainer}>
      {showTitle && <h4>{translateString('Data Model')}</h4>}
      {showSearch && (
        <Search
          handleSearch={debouncedHandleSearch}
          clearSearch={clearSearch}
          handleSelectField={handleSelectField}
        />
      )}
      <div className={classes.treeContainer} ui-ta={getUITA('DataModel', 'section')}>
        {showDataModelData && (
          <TreeView
            variant="folder"
            defaultExpanded={[defaultEl]}
            defaultSelected={['1']}
            onNodeSelect={handleSelect}
            onNodeToggle={handleExpandedNodes}
            expanded={expanded}
            multiSelect
          >
            {dataTree.map((node) => renderTree(node, searchText))}
          </TreeView>
        )}
      </div>
    </div>
  );
};

export default React.memo(EntityModel);
